import type { LucideIcon } from "lucide-react";

import {
  isModuleAuthorized,
  moduleLandingPath,
  type ModuleDefinition,
  type ModuleKey,
} from "@/lib/module-registry";
import { LAUNCHER_MODULES } from "@/lib/launcher-modules";
import { registerSectionLabels } from "@/lib/routes";
import type { SessionFeatureFlags } from "@/types/generated/SessionFeatureFlags";
import { ADMIN_MODULE } from "./admin-module";
import { DEAL_CAPTURE_MODULE } from "./deal-capture-module";
import { REPORTS_MODULE } from "./reports-module";
import { ENERGY_ADVISOR_MODULE } from "./energy-advisor-module";
import { MARKET_INTELLIGENCE_MODULE } from "./market-intelligence-module";
import { OPERATIONS_MODULE } from "./operations-module";

/**
 * Every built module, in launcher order.
 *
 * **Adding a module is one file plus one line here.** That's the point of the
 * registry: the previous scheme needed edits across eight files (an
 * `AppRoute` boolean, a section union, a label map, the merged label map, a
 * routes file with its own copy of `get*NavSections`, a near-verbatim
 * sidebar, `router.tsx`'s spread and handle stamp, and `AppShell`'s flag
 * extractor plus a cascade branch).
 *
 * Modules that appear in the launcher but aren't built yet — Budget Planning,
 * Energy Insights — live only in
 * `lib/launcher-modules.tsx`. They render as disabled "coming soon" tiles with
 * no routes, no sidebar, and no entry here until they're real. Presence in
 * this array IS what makes a module reachable.
 */
export const MODULES: ModuleDefinition[] = [
  ENERGY_ADVISOR_MODULE,
  MARKET_INTELLIGENCE_MODULE,
  OPERATIONS_MODULE,
  REPORTS_MODULE,
  ADMIN_MODULE,
  DEAL_CAPTURE_MODULE,
];

// Publish every module's section labels so `routeMetaFor` can resolve a
// breadcrumb's section heading for any route, whichever module declared it.
// Pushed rather than imported to keep `lib/routes.tsx` free of a dependency
// on the registry (the registry already imports `AppRoute` from it).
registerSectionLabels(
  MODULES.flatMap((mod) =>
    mod.sections
      .filter((s) => s.label !== undefined)
      .map((s) => [s.key, s.label!] as [string, string]),
  ),
);

const BY_KEY: Map<string, ModuleDefinition> = new Map(
  MODULES.map((a) => [a.key as string, a]),
);

/** Look up a module by key, or `undefined` when the key is unknown. */
export function findModule(key: string | undefined): ModuleDefinition | undefined {
  return key ? BY_KEY.get(key) : undefined;
}

/**
 * Look up a module by key, falling back to Sales.
 *
 * Used for chrome selection, where there is always some module to render: a
 * cold deep link to a shared record has no module context, and a stale
 * `localStorage` key (a module that was renamed or removed) must not leave the
 * user with no sidebar at all.
 */
export function moduleOrDefault(key: string | undefined): ModuleDefinition {
  return findModule(key) ?? ENERGY_ADVISOR_MODULE;
}

/**
 * The module a shared record (a client, deal or account) should open in when
 * the user jumps to it from search.
 *
 * A record route carries no `moduleKey`, so it renders in whichever module is
 * active. From Market Intelligence, Reports or Admin that would be a client
 * page with no record bar, in a module that doesn't work with clients. So:
 * the current module when it shows the record bar and the caller can open it;
 * otherwise the first record-bar module they can open, in `MODULES` order
 * (Energy Advisor, then Operations). When they can open neither, the current
 * module, so the record still opens in the chrome they were in. (AB#8790)
 */
export function recordModuleFor(
  current: ModuleKey,
  permissions?: { granted: string[]; is_admin: boolean },
  isSuperAdmin: boolean = false,
): ModuleKey {
  const opens = (m: ModuleDefinition) =>
    m.recordBar === true && isModuleAuthorized(m, permissions, isSuperAdmin);
  if (opens(moduleOrDefault(current))) return current;
  return MODULES.find(opens)?.key ?? current;
}

/**
 * One row per module for the switcher and the Modules picker, **sorted A–Z by
 * label** with built and unbuilt interleaved.
 *
 * Alphabetical across the whole list, not built-first-then-unbuilt: these
 * surfaces are for *finding* a module, and a user scanning for "Reports"
 * shouldn't have to know whether it's built to know where to look. Grouping
 * by build state also means a tile jumps position the day it ships.
 *
 * `href` is `undefined` for a module the caller cannot open — callers render
 * those disabled with a lock. Two different reasons produce that state:
 *
 *  - **Not built yet.** No routes exist, so there is nowhere to send anyone.
 *  - **Built but not entitled.** The module's own feature flag is off. Market
 *    Intelligence is the case this exists for: market data is a paid
 *    entitlement, so a tenant without it should still SEE the module and be
 *    told it is locked. That is the opposite of how every other flag behaves —
 *    the rest hide their surface so a tenant cannot tell the feature exists —
 *    and it is deliberate, because a lock on a purchasable module is an upsell
 *    rather than a leak.
 *
 * Built, entitled modules land where `moduleLandingPath` sends this caller:
 * the registry's `entryPath`, or their first sidebar page when that one is
 * gated for them. A module the caller can open but has no page in is
 * left out, the same as one they cannot open.
 */
export interface ModuleMenuEntry {
  key: string;
  label: string;
  icon: LucideIcon;
  description: string;
  href?: string;
}

/** Partial so callers can pass just the flags a test cares about; a missing
 *  flag reads as off. */
type FeatureFlagState = Partial<SessionFeatureFlags>;

export function moduleMenuEntries(
  /**
   * The caller's resolved flags, for routes gated on one. Omit in a context
   * with no session (tests of pure ordering).
   */
  features?: FeatureFlagState,
  /**
   * The caller's permissions. A module whose access permission they lack is
   * left out entirely — hidden, not locked: a locked tile is a module that
   * is not built yet (`LAUNCHER_MODULES`). Omit only
   * where there is no session (pure ordering tests), and every built module
   * is listed.
   */
  permissions?: { granted: string[]; is_admin: boolean },
  /**
   * The caller's role facts, for the routes gated on them. Only read when
   * `permissions` is given; omitted, both read as false.
   */
  roles?: { isSuperAdmin: boolean; isSupplier: boolean },
): ModuleMenuEntry[] {
  const caller = permissions && {
    isSuperAdmin: roles?.isSuperAdmin ?? false,
    isSupplier: roles?.isSupplier ?? false,
    features: (features ?? {}) as SessionFeatureFlags,
    permissions,
  };
  const built = new Set<string>(MODULES.map((a) => a.key));
  const entries: ModuleMenuEntry[] = [
    ...MODULES.filter(
      (a) => !permissions || isModuleAuthorized(a, permissions, roles?.isSuperAdmin ?? false),
    ).flatMap(
      (a): ModuleMenuEntry[] => {
        const landing = moduleLandingPath(a, caller);
        // Nothing in it this caller can open: hidden, the same as a module
        // they lack access to.
        if (landing === undefined) return [];
        return [
          {
            key: a.key as string,
            label: a.label,
            icon: a.icon,
            description:
              a.description ??
              LAUNCHER_MODULES.find((l) => l.key === a.key)?.description ??
              "",
            href: landing,
          },
        ];
      },
    ),
    ...LAUNCHER_MODULES.filter((l) => !built.has(l.key)).map((l) => ({
      key: l.key,
      label: l.label,
      icon: l.icon,
      description: l.description,
      href: undefined as string | undefined,
    })),
  ];
  return entries.sort((a, b) => a.label.localeCompare(b.label));
}

export type { ModuleDefinition, ModuleKey };
export {
  ADMIN_MODULE,
  DEAL_CAPTURE_MODULE,
  ENERGY_ADVISOR_MODULE,
  OPERATIONS_MODULE,
  REPORTS_MODULE,
};
